// App.js
import React, { useEffect, useState, useCallback } from "react";
import UserTable from "./ui/UserTable.js";
import AddUserModal from "./ui/AddUserModal.js";
import AdminLoginForm from "./ui/AdminLoginForm.js"; // <- shows login, sets token via onSuccess
import { clearToken, createUser, deleteUser, getToken, listUsers, issueTempPassword } from "./non-ui/ApiClient.js";

export default function App() {
  const h = React.createElement;

  // auth gate (no Firebase SDK): token presence = "signed in"
  const [hasToken, setHasToken] = useState(!!getToken());

  // page state
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadUsers = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setErr("");
      const data = await listUsers(filters);
      const normalized = (data.users || []).map((u) => ({
        id: u.uid,
        name: u.name,
        email: u.email,
        org: u.organisation,
        role: u.role || "user",
      }));
      setUsers(normalized);
    } catch (e) {
      // if auth died, bounce to login
      if (e.status === 401 || e.status === 403 || /Unauthorized|Forbidden/i.test(e.message)) {
        clearToken();
        setHasToken(false);
        return;
      }
      setErr(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasToken) loadUsers();
  }, [hasToken, loadUsers]);

  async function handleDelete(id) {
    if (!window.confirm("Delete this user?")) return;
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (e) {
      if (e.status === 401 || e.status === 403) { clearToken(); setHasToken(false); return; }
      alert(e.message || "Delete failed");
    }
  }

  async function handleSave(newUser) {
    // newUser: { name, email, password, role, organisation }
    try {
      const res = await createUser(newUser);
      setUsers((prev) => [
        {
          id: res.uid,
          name: newUser.name,
          email: newUser.email,
          org: newUser.organisation,
          role: newUser.role || "user",
        },
        ...prev,
      ]);
      setShowAdd(false);
    } catch (e) {
      if (e.status === 401 || e.status === 403) { clearToken(); setHasToken(false); return; }
      alert(e.message || "Create failed");
    }
  }

  // Optional: wire reset password button from UserTable (if you pass onResetPassword)
  async function handleResetPassword(user) {
    try {
      // can pass user.id (uid) or user.email
      return await issueTempPassword(user.email);
    } catch (e) {
      if (e.status === 401 || e.status === 403) { clearToken(); setHasToken(false); return; }
      throw e;
    }
  }

  function handleLogout() {
    clearToken();
    setHasToken(false);
  }

  // ---- render gates ----
  if (!hasToken) {
    return h(AdminLoginForm, { onSuccess: () => setHasToken(true) });
  }

  return h(
    React.Fragment,
    null,
    // tiny header with logout
    h("div", { style: { padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f6f8fb", borderBottom: "1px solid #e9edf3" } },
      h("div", { style: { fontWeight: 600 } }, "Admin Console"),
      h("button", { className: "btn", onClick: handleLogout }, "Logout")
    ),
    loading ? h("div", { className: "bar" }, "Loading…") : null,
    err ? h("div", { className: "error" }, err) : null,
    h(UserTable, {
      users,
      onDelete: handleDelete,
      onAdd: () => setShowAdd(true),
      onResetPassword: handleResetPassword, // remove if your table doesn’t use it
    }),
    showAdd
      ? h(AddUserModal, { onClose: () => setShowAdd(false), onSave: handleSave })
      : null
  );
}
